import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import QRCodeScanner from '../../../components/event/QRCodeScanner';
import { getEventById } from '../../../services/event.service';
import { Event, EventAttendee } from '../../../types/event';
import { useToast } from '../../../hooks/useToast';
import { formatDate } from '../../../utils/dateFormatter';

const TicketVerification: React.FC = () => {
  // Ne pas utiliser useParams car cette page est accessible sans ID d'événement
  const { showToast } = useToast();
  
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState({
    scanned: 0,
    total: 0
  });
  
  // Pas besoin de charger un événement spécifique pour cette page
  useEffect(() => {
    setLoading(false);
  }, []);
  
  const handleScanSuccess = (attendee: EventAttendee) => {
    if (attendee.status === 'checked_in') {
      // Already checked in
      setStats(prev => ({
        ...prev,
        scanned: prev.scanned
      }));
    } else {
      // New check-in
      setStats(prev => ({
        ...prev,
        scanned: prev.scanned + 1
      }));
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  // Pas besoin de vérifier si l'événement existe
  
  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <Link
            to="/events/admin"
            className="text-yellow-400 hover:text-yellow-300 flex items-center text-sm"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Retour aux événements
          </Link>
        </div>
      <div className="min-h-screen bg-gray-900 px-4 sm:px-6 lg:px-8 py-12 flex flex-col items-center">
        <div className="w-full max-w-4xl">
          <div className="mb-10 text-center">
            <h1 className="text-4xl font-extrabold text-white tracking-tight mb-3">Vérification des Tickets</h1>
            <p className="text-lg text-gray-300">Scannez le QR code du participant pour valider sa présence</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Colonne de gauche */}
            <div className="col-span-1 space-y-6">
              {/* Carte Statistiques */}
              <div className="bg-gray-800/80 border border-gray-700/50 rounded-xl shadow-lg p-6 backdrop-blur-sm">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-yellow-500/10 p-3 rounded-full mb-3">
                    <svg className="h-8 w-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M9 20H4v-2a3 3 0 015.356-1.857M15 10a4 4 0 10-8 0 4 4 0 008 0zm6 6v2a2 2 0 01-2 2h-2.5M9 20v-2a2 2 0 012-2h2a2 2 0 012 2v2" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-200 mb-1">Participants enregistrés</h2>
                  <p className="text-4xl font-bold text-yellow-400">{stats.scanned}</p>
                </div>
              </div>
              
              {/* Carte Instructions */}
              <div className="bg-gray-800/80 border border-gray-700/50 rounded-xl shadow p-6 backdrop-blur-sm">
                <h2 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
                  <svg className="h-5 w-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Instructions
                </h2>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">1.</span>
                    <span>Scannez le <span className="font-medium text-cyan-300">QR code</span> du ticket</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">2.</span>
                    <span>Vérifiez les informations affichées</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">3.</span>
                    <span>Cliquez sur <span className="font-medium text-green-400">"Enregistrer"</span> pour valider</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">4.</span>
                    <span>Un ticket déjà utilisé sera marqué <span className="font-medium text-yellow-400">"Déjà enregistré"</span></span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Scanner QR */}
            <div className="md:col-span-2">
              <div className="w-full h-full bg-gray-800/70 border border-gray-700/50 rounded-2xl shadow-xl p-6 backdrop-blur-sm">
                <div className="h-full flex flex-col">
                  <h3 className="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
                    <svg className="h-5 w-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                    Scanner un ticket
                  </h3>
                  <div className="flex-1 flex items-center justify-center bg-gray-900/50 rounded-xl border-2 border-dashed border-gray-700/70 p-4">
                    <QRCodeScanner onSuccess={handleScanSuccess} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
};

export default TicketVerification;
